/**
 * Who may join Quarry: the rules the sign-up trigger, the API and the browser share.
 *
 * Deliberately free of imports. The Cognito sign-up trigger has five seconds
 * and weighs a few hundred bytes. Importing anything else from this package
 * brings its zod schemas and its domain table along, which is over half a
 * megabyte of cold start. So this file is also published on its own, as
 * `@profiler/shared/access`, and the trigger imports only that.
 */
/** The only domain whose people are Habilelabs staff. */
export const STAFF_DOMAIN = 'habilelabs.io';
/**
 * The settings-table row holding the invitations.
 *
 * infra imports this for the trigger's IAM condition. The trigger may read
 * this one row and nothing else, so there is exactly one spelling, and a
 * rename moves the permission with it.
 */
export const ACCESS_SETTING_KEY = 'users#access';
/** How many addresses one invitation may carry: a team pasted from Outlook, not a mailing list. */
export const INVITE_BATCH_MAX = 50;
/**
 * The joining mode an environment value names.
 *
 * Only the exact word `entra` keeps the old gate. Anything else, a typo
 * included, reads as `invitation`. A wrongly refused sign-in is noticed within
 * the day. A door left open is not noticed at all.
 */
export function readJoiningMode(value) {
    return value === 'entra' ? 'entra' : 'invitation';
}
export function normaliseEmail(email) {
    return email.trim().toLowerCase();
}
/**
 * Exact domain match, not a registrable-domain match.
 *
 * `registrableDomain` would fold `anything.habilelabs.io` into `habilelabs.io`
 * and admit a subdomain we do not issue mailboxes on.
 */
export function isAllowedEmail(email) {
    if (!email)
        return false;
    const at = email.lastIndexOf('@');
    if (at < 0)
        return false;
    return (email
        .slice(at + 1)
        .trim()
        .toLowerCase() === STAFF_DOMAIN);
}
/** Letters, digits and the punctuation a mailbox name can carry. No spaces, no second `@`. */
const LOCAL_PART = /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*$/;
/**
 * An address somebody can be invited under.
 *
 * Stricter than `isAllowedEmail`, which only has to recognise a staff address
 * that Entra has already verified. This one is typed or pasted by a person, so
 * it also refuses the near misses: two `@`s, a dotted end, a subdomain.
 */
export function isInvitableEmail(email) {
    const parts = normaliseEmail(email).split('@');
    if (parts.length !== 2)
        return false;
    const [local = '', domain = ''] = parts;
    return LOCAL_PART.test(local) && domain === STAFF_DOMAIN;
}
/**
 * The addresses in whatever somebody pasted.
 *
 * Takes the forms people actually paste: one per line, separated by commas or
 * semicolons, and Outlook's `Name <address>`, including a quoted name that
 * contains a comma. Lowercased and without repeats. Whatever is not an address
 * Quarry can invite comes back as `invalid`, so it can be shown rather than
 * dropped.
 */
export function parseInviteEmails(text) {
    const found = [];
    const rest = text.replace(/"[^"]*"\s*<([^<>]*)>|<([^<>]*)>/g, (_match, quoted, bare) => {
        found.push(quoted ?? bare ?? '');
        return ' ';
    });
    // What is left is bare addresses and the unquoted names that stood in front
    // of angled ones. A name is not an address, so a token without an `@` goes.
    for (const token of rest.split(/[\s,;]+/))
        if (token.includes('@'))
            found.push(token);
    const seen = new Set();
    const valid = [];
    const invalid = [];
    for (const raw of found) {
        const email = normaliseEmail(raw.replace(/^[("'[]+|[)"'\].]+$/g, ''));
        if (!email || seen.has(email))
            continue;
        seen.add(email);
        if (isInvitableEmail(email))
            valid.push(email);
        else
            invalid.push(email);
    }
    return { valid, invalid };
}
/**
 * The invitations in a settings row, by lowercased address.
 *
 * Strict about the key and lenient about the rest. An entry whose key is not an
 * address Quarry could have invited is left out rather than trusted, because
 * this map is what lets somebody in.
 */
export function readInvites(item) {
    const out = new Map();
    const invites = item?.invites;
    if (!invites || typeof invites !== 'object' || Array.isArray(invites))
        return out;
    for (const [key, value] of Object.entries(invites)) {
        if (!isInvitableEmail(key))
            continue;
        const entry = (value ?? {});
        out.set(normaliseEmail(key), {
            invitedBy: typeof entry['invitedBy'] === 'string' ? entry['invitedBy'] : '',
            invitedAt: typeof entry['invitedAt'] === 'string' ? entry['invitedAt'] : '',
        });
    }
    return out;
}
/**
 * Why the sign-up trigger refused somebody, in our own words.
 *
 * Without a closing full stop: Cognito appends ". " to whatever the trigger
 * throws, and a sentence that already ends in one arrives as "..".
 */
export const SIGN_UP_REFUSALS = {
    notStaff: 'Sign-in is limited to habilelabs.io accounts',
    noEmail: 'Your Microsoft account did not give Quarry an email address, so Quarry cannot tell who you are. Ask IT to check the account has a mailbox',
    unavailable: 'Quarry could not check its invitations just now. Try again in a minute',
};
/** The refusal for somebody who is staff but was never invited. It names the account, because the fix depends on it. */
export function notInvitedRefusal(email) {
    return `You signed in as ${email}, which has not been invited to Quarry. Ask an administrator to invite this address, or sign in with the account they invited`;
}
/**
 * Our refusal inside whatever description Cognito sent back, or null.
 *
 * Matched on our own sentences, never on Cognito's wrapper. AWS says its error
 * descriptions are not fixed strings. The two formats seen so far are
 * "PreSignUp failed with error <ours>." and the same with a trailing ". ".
 */
export function signUpRefusalIn(description) {
    if (!description)
        return null;
    for (const kind of Object.keys(SIGN_UP_REFUSALS)) {
        if (description.includes(SIGN_UP_REFUSALS[kind]))
            return { kind, message: `${SIGN_UP_REFUSALS[kind]}.` };
    }
    const invited = /You signed in as (\S+?), which has not been invited to Quarry/.exec(description);
    if (invited?.[1])
        return { kind: 'notInvited', message: `${notInvitedRefusal(invited[1])}.` };
    return null;
}
//# sourceMappingURL=access.js.map